// Cached Workamajig lookups for the cascading selects. Clients are global;
// projects, tasks and services are cached per parent id. App settings
// (config/settings.json) are cached here too — the Service select needs them.
//
// Three rules, learned the hard way:
//
//  1. NOTHING is cached for more than an hour. These lists change in Workamajig
//     without telling us, and a tab left open all day was serving whatever it
//     fetched at 9am.
//  2. A FAILURE IS NEVER CACHED, and neither is an empty list. Caching the
//     promise itself meant one 403 (hit before the user's onboarding finished) or
//     one 409 (integration not yet connected) left the dropdown empty for the
//     rest of the session — which is exactly what first-time users saw.
//  3. reset() is called on logout and when onboarding completes. Both navigate
//     via pushState with no page reload, so this module's state outlives them;
//     without the reset, the next user in the same tab inherits the previous
//     user's service list.

import { api } from './api.js'

// The ceiling from the server side (services/workamajig.service.js) applies here
// too — a browser cache is still a cache.
const TTL_MS = 60 * 60 * 1000

// One cache per lookup: Map<key, { at, value }>. Only RESOLVED, non-empty values
// are ever stored, so a rejection can't become sticky.
const caches = new Map()
// In-flight requests, keyed the same way, so concurrent rows asking for the same
// list share one request without that promise outliving a failure.
const inFlight = new Map()

function cacheFor(name) {
  if (!caches.has(name)) caches.set(name, new Map())
  return caches.get(name)
}

// Read-through fetch with a TTL. `fetcher` runs only on a miss; its result is
// cached only when it resolves to a non-empty array. On failure the entry is left
// absent and the error propagates, so the caller can report it and the next
// attempt retries.
function cached(name, key, fetcher) {
  const store = cacheFor(name)
  const hit = store.get(key)
  if (hit && Date.now() - hit.at < TTL_MS) return Promise.resolve(hit.value)
  if (hit) store.delete(key)

  const flightKey = `${name}:${key}`
  if (inFlight.has(flightKey)) return inFlight.get(flightKey)

  const promise = fetcher()
    .then(value => {
      if (Array.isArray(value) ? value.length : value) store.set(key, { at: Date.now(), value })
      return value
    })
    .finally(() => inFlight.delete(flightKey))

  inFlight.set(flightKey, promise)
  return promise
}

// Drop everything. Called when the signed-in user changes — see rule 3 above.
// `settingsPromise`/`settingsAt` are declared further down, next to settings();
// this runs long after the module has evaluated, so the reference is fine.
export function reset() {
  caches.clear()
  inFlight.clear()
  settingsPromise = null
  settingsAt = 0
}

export function clients() {
  return cached('clients', '', () => api.get('/workamajig/clients'))
}

// The services bookable to a job. The SERVER narrows this by
// config/settings.json, so an enforced job comes back with one option and a job
// that forbids a service never receives it — cached per job, since each job
// gets its own answer. Called with no job, it's the unnarrowed list.
export function services(projectId = null) {
  const key = projectId || ''
  const query = key ? `?projectId=${encodeURIComponent(key)}` : ''
  return cached('services', key, () => api.get(`/workamajig/services${query}`))
}

// Server settings from config/settings.json. Degrades to empty rather than
// failing a row's option load — the server narrows the service list by the same
// rules regardless. A FAILURE IS NOT CACHED: caching the empty fallback would
// leave every row for the rest of the session believing there are no rules,
// which is precisely how a transient 401 turns into "the policy stopped working".
//
// Held as a bare promise rather than going through cached() because the fallback
// value is a deliberate non-throwing default, which cached() would treat as a
// successful (and therefore cacheable) result. The hour still applies: the file is
// edited by hand and read at server startup, so a restart must reach open tabs.
let settingsPromise = null
let settingsAt = 0

export function settings() {
  if (settingsPromise && Date.now() - settingsAt >= TTL_MS) settingsPromise = null
  if (!settingsPromise) {
    settingsAt = Date.now()
    settingsPromise = api.get('/settings').catch(err => {
      console.warn('Ziggy: could not load /api/settings; service rules unavailable client-side', err)
      settingsPromise = null
      return { enforcedServices: {}, restrictedServices: [] }
    })
  }
  return settingsPromise
}

// The one service code a job is pinned to in config/settings.json, or null when
// the service is the user's to choose. Keys are normalized server-side (trimmed
// and upper-cased), so normalize the project number the same way.
export async function enforcedService(projectId) {
  if (!projectId) return null
  const { enforcedServices } = await settings()
  return enforcedServices?.[String(projectId).trim().toUpperCase()] || null
}

// The restriction rules, most specific first:
//   [{ service: '<code>' | servicePrefix: '<prefix>', jobs: ['<JOB>', …] }]
// A listed service may only be booked to its jobs; anything no rule matches is
// unrestricted.
export async function serviceRestrictions() {
  const { restrictedServices } = await settings()
  return Array.isArray(restrictedServices) ? restrictedServices : []
}

// Whether a service may be booked to a job, per those rules. Both of the
// service's identifiers are tried — Workamajig gives each one a code and a
// description, the dropdown shows the description, and config may name either —
// and the first matching rule governs, mirroring lib/settings.js. With no job
// selected nothing can be judged, so everything passes.
export function serviceAllowedOnJob(rules, service, projectId) {
  const job = String(projectId || '').trim().toUpperCase()
  if (!job || !rules?.length) return true
  const keys = [service?.code, service?.name]
    .map(v => String(v ?? '').trim().toLowerCase())
    .filter(Boolean)
  if (!keys.length) return true
  for (const rule of rules) {
    const hit = rule.servicePrefix != null
      ? keys.some(k => k.startsWith(rule.servicePrefix))
      : keys.some(k => k === rule.service)
    if (hit) return (rule.jobs || []).includes(job)
  }
  return true
}

export function projects(clientId) {
  if (!clientId) return Promise.resolve([])
  return cached('projects', clientId, () =>
    api.get(`/workamajig/clients/${encodeURIComponent(clientId)}/projects`))
}

export function tasks(projectId) {
  if (!projectId) return Promise.resolve([])
  return cached('tasks', projectId, () =>
    api.get(`/workamajig/projects/${encodeURIComponent(projectId)}/tasks`))
}
